import React, { Component } from 'react';
import { Modal, Button, Form, ListGroup, Alert, ProgressBar } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getSocketApiBase, getLoginApiBase } from '../utils/apiBase';

class UploadComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedFile: null,
            uploading: false,
            uploadProgress: 0,
            userFiles: [],
            loadingFiles: false,
            videoTitle: '',
            videoDescription: '',
            categories: []
        };
    }

    componentDidMount() {
        if (this.props.show && this.props.userEmail) {
            this.loadUserFiles();
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.show && !prevProps.show && this.props.userEmail) {
            this.loadUserFiles();
        }
    }

    loadUserFiles = async () => {
        this.setState({ loadingFiles: true });
        try {
            const userId = this.getUserId();
            if (!userId) {
                this.setState({ loadingFiles: false });
                return;
            }

            const response = await axios.get(
                `${getLoginApiBase()}/user/${userId}/videos`
            );
            this.setState({
                userFiles: response.data.data.videos || [],
                loadingFiles: false
            });
        } catch (error) {
            console.error('Error loading files:', error);
            this.setState({ loadingFiles: false });
        }
    };

    getUserId = () => sessionStorage.getItem('user_id');

    handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file && file.size > 100 * 1024 * 1024) {
            toast.error('File size must be less than 100MB');
            return;
        }
        this.setState({ selectedFile: file });
    };

    handleUpload = async () => {
        const { selectedFile, videoTitle, videoDescription, categories } = this.state;
        const { userEmail } = this.props;
        const userId = this.getUserId();

        if (!selectedFile) {
            toast.error('Please select a file to upload');
            return;
        }

        if (!videoTitle.trim()) {
            toast.error('Please enter a video title');
            return;
        }

        if (!userEmail || !userId) {
            toast.error('Please sign in to upload files');
            return;
        }

        const formData = new FormData();
        formData.append('file', selectedFile);

        this.setState({ uploading: true, uploadProgress: 0 });

        try {
            const uploadResponse = await axios.post(
                `${getSocketApiBase()}/upload/${userId}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    onUploadProgress: (progressEvent) => {
                        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        this.setState({ uploadProgress: progress });
                    }
                }
            );

            const uploadedFile = uploadResponse.data?.file;
            if (!uploadedFile?.path) {
                throw new Error('Upload response missing file path');
            }

            const videoData = {
                videoFilename: selectedFile.name,
                videoTitle: videoTitle.trim(),
                videoDescription: videoDescription.trim(),
                fileSize: selectedFile.size,
                minioPath: uploadedFile.path,
                categories: categories,
                isPublic: true
            };

            await axios.post(
                `${getLoginApiBase()}/user/${userId}/videos`,
                videoData
            );

            toast.success('Video uploaded and saved successfully!');
            this.setState({
                selectedFile: null,
                uploading: false,
                uploadProgress: 0,
                videoTitle: '',
                videoDescription: '',
                categories: []
            });

            this.loadUserFiles();

            const fileInput = document.getElementById('fileInput');
            if (fileInput) {
                fileInput.value = '';
            }
        } catch (error) {
            console.error('Upload error:', error);
            toast.error('Failed to upload video');
            this.setState({ uploading: false, uploadProgress: 0 });
        }
    };

    handleDelete = async (video) => {
        const userId = this.getUserId();

        if (!window.confirm(`Are you sure you want to delete "${video.videoTitle}"?`)) {
            return;
        }

        try {
            await axios.delete(
                `${getLoginApiBase()}/user/${userId}/videos/${video.id}`
            );

            if (video.minioPath) {
                await axios.delete(
                    `${getSocketApiBase()}/user-upload/${userId}`,
                    { params: { path: video.minioPath } }
                );
            }

            toast.success('Video deleted successfully');
            this.loadUserFiles();
        } catch (error) {
            console.error('Delete error:', error);
            toast.error('Failed to delete video');
        }
    };

    handleInputChange = (field, value) => {
        this.setState({ [field]: value });
    };

    formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    render() {
        const { show, onHide } = this.props;
        const { selectedFile, uploading, uploadProgress, userFiles, loadingFiles, videoTitle, videoDescription } = this.state;

        return (
            <Modal show={show} onHide={onHide} size="lg" centered className="modern-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Upload Files</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Video Title *</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter video title"
                                value={videoTitle}
                                onChange={(e) => this.handleInputChange('videoTitle', e.target.value)}
                                disabled={uploading}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder="Enter video description (optional)"
                                value={videoDescription}
                                onChange={(e) => this.handleInputChange('videoDescription', e.target.value)}
                                disabled={uploading}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Select video file *</Form.Label>
                            <Form.Control
                                id="fileInput"
                                type="file"
                                accept="video/*"
                                onChange={this.handleFileSelect}
                                disabled={uploading}
                            />
                            <Form.Text className="text-muted">
                                Maximum file size: 100MB. Supported formats: MP4, AVI, MOV, etc.
                            </Form.Text>
                        </Form.Group>

                        {selectedFile && (
                            <Alert variant="info">
                                Selected: {selectedFile.name} ({this.formatFileSize(selectedFile.size)})
                            </Alert>
                        )}

                        {uploading && (
                            <ProgressBar
                                now={uploadProgress}
                                label={`${uploadProgress}%`}
                                animated
                                className="my-3"
                            />
                        )}

                        <Button
                            variant="primary"
                            onClick={this.handleUpload}
                            disabled={!selectedFile || !videoTitle.trim() || uploading}
                            className="mb-4"
                        >
                            {uploading ? 'Uploading...' : 'Upload Video'}
                        </Button>
                    </Form>

                    <hr />

                    <h5>Your Files</h5>
                    {loadingFiles ? (
                        <div className="text-center">Loading files...</div>
                    ) : userFiles.length === 0 ? (
                        <Alert variant="info">No files uploaded yet</Alert>
                    ) : (
                        <ListGroup>
                            {userFiles.map((video, index) => (
                                <ListGroup.Item
                                    key={video.id || index}
                                    className="d-flex justify-content-between align-items-center"
                                >
                                    <div>
                                        <strong>{video.videoTitle}</strong>
                                        <br />
                                        <small className="text-muted">
                                            {video.videoFilename} • {this.formatFileSize(video.fileSize)} •
                                            {new Date(video.uploadDate).toLocaleDateString()}
                                        </small>
                                        {video.videoDescription && (
                                            <div className="mt-1">
                                                <small className="text-secondary">{video.videoDescription}</small>
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <Button
                                            variant="outline-success"
                                            size="sm"
                                            className="me-2"
                                        >
                                            {video.views || 0} views
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => this.handleDelete(video)}
                                        >
                                            Delete
                                        </Button>
                                    </div>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
}

export default UploadComponent;
